import { Checkbox } from 'antd';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import {
  BookNode,
  LessonWithAssignments,
} from '../../../../pages/lms/assignment-mgmt/CreateAssignment';
import TextWinds from '../../../common/TextWinds';
import {
  SUBJECT_COLOR_MAP,
  SUBJECT_LABEL_MAP,
} from '../../../../constants/types/lms/type';

interface RecitationSelectionPanelProps {
  bookNodes: BookNode[];
  selectedAssignmentIds: Set<number>;
  openedBookIds: Set<string>;
  openedLessonIds: Set<string>;
  onBookOpen: (bookId: string) => void | Promise<void>;
  onLessonOpen: (bookId: string, lessonId: string) => void | Promise<void>;
  onBookSelect: (bookId: string) => void | Promise<void>;
  onLessonSelect: (bookId: string, lessonId: string) => void | Promise<void>;
  onAssignmentSelect: (assignmentId: number) => void;
}

const getBookSelectionState = (
  book: BookNode,
  selectedAssignmentIds: Set<number>,
) => {
  const lessons = book.lessons ?? [];

  const isBookFullyLoaded =
    book.isLessonLoaded &&
    lessons.length > 0 &&
    lessons.every((lesson) => lesson.isAssignmentLoaded);

  const loadedAssignmentIds = lessons.flatMap(
    (lesson) => lesson.assignments?.map((assignment) => assignment.id) ?? [],
  );

  const hasSelectedAssignment = loadedAssignmentIds.some((id) =>
    selectedAssignmentIds.has(id),
  );

  const isAllLoadedAssignmentsSelected =
    loadedAssignmentIds.length > 0 &&
    loadedAssignmentIds.every((id) => selectedAssignmentIds.has(id));

  const checked = isBookFullyLoaded && isAllLoadedAssignmentsSelected;
  const indeterminate = !checked && hasSelectedAssignment;

  return {
    checked,
    indeterminate,
  };
};

const getLessonAssignmentIds = (lesson: LessonWithAssignments) => {
  return lesson.assignments?.map((assignment) => assignment.id) ?? [];
};

const isAllSelected = (assignmentIds: number[], selectedIds: Set<number>) => {
  if (assignmentIds.length === 0) return false;

  return assignmentIds.every((id) => selectedIds.has(id));
};

const isPartiallySelected = (
  assignmentIds: number[],
  selectedIds: Set<number>,
) => {
  if (assignmentIds.length === 0) return false;

  const selectedCount = assignmentIds.filter((id) =>
    selectedIds.has(id),
  ).length;

  return selectedCount > 0 && selectedCount < assignmentIds.length;
};

/**
 * 개편 LMS (교사) - AI인증제 관리 > AI인증제 내주기 페이지 > 콘텐츠 영역 > 과제 선택 판넬
 */
const RecitationSelectionPanel = ({
  bookNodes,
  selectedAssignmentIds,
  openedBookIds,
  openedLessonIds,
  onBookOpen,
  onLessonOpen,
  onBookSelect,
  onLessonSelect,
  onAssignmentSelect,
}: RecitationSelectionPanelProps) => {
  return (
    <section className="flex h-[480px] flex-col rounded-xl bg-white p-4 shadow-card xl:h-[calc(100vh_-_180px)] xl:p-6">
      <div className="mb-5 flex shrink-0 items-start justify-between gap-4">
        <TextWinds type="title_h3">과제 선택</TextWinds>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
        <div className="flex flex-col gap-3">
          {bookNodes.map((book) => {
            const bookSelectionState = getBookSelectionState(
              book,
              selectedAssignmentIds,
            );
            const isBookOpened = openedBookIds.has(book.id);

            return (
              <div
                key={book.id}
                className="overflow-hidden rounded-xl border border-gray3 bg-white transition-shadow hover:shadow-sm"
              >
                <div className="flex items-center justify-between gap-3 bg-gray1 px-4 py-3">
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-center gap-1 text-left xl:gap-3"
                    onClick={() => onBookOpen(book.id)}
                  >
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white text-gray7 shadow-sm">
                      {isBookOpened ? (
                        <ChevronDownIcon className="h-5 w-5" />
                      ) : (
                        <ChevronRightIcon className="h-5 w-5" />
                      )}
                    </span>

                    <div
                      className={[
                        'flex h-12 w-12 shrink-0 items-center justify-center whitespace-pre-line rounded-xl text-center font-extrabold leading-tight',
                        SUBJECT_COLOR_MAP[book.type],
                      ].join(' ')}
                    >
                      {SUBJECT_LABEL_MAP[book.type]}
                    </div>

                    <div className="min-w-0">
                      <TextWinds type="content_body2" className="font-bold">
                        {book.title}
                      </TextWinds>
                    </div>
                  </button>

                  <Checkbox
                    checked={bookSelectionState.checked}
                    indeterminate={bookSelectionState.indeterminate}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => onBookSelect(book.id)}
                  >
                    <TextWinds type="caption1" color="gray8">
                      전체 선택
                    </TextWinds>
                  </Checkbox>
                </div>

                {isBookOpened && book.isLessonLoaded && (
                  <div className="flex flex-col bg-white">
                    {book.lessons?.map((lesson) => {
                      const lessonAssignmentIds =
                        getLessonAssignmentIds(lesson);

                      const lessonAllSelected = isAllSelected(
                        lessonAssignmentIds,
                        selectedAssignmentIds,
                      );

                      const lessonPartiallySelected = isPartiallySelected(
                        lessonAssignmentIds,
                        selectedAssignmentIds,
                      );

                      const isLessonOpened = openedLessonIds.has(lesson.id);

                      return (
                        <div key={lesson.id} className="border-t border-gray2">
                          <div className="flex items-center justify-between gap-3 px-4 py-3">
                            <button
                              type="button"
                              className="flex min-w-0 flex-1 items-center gap-2 text-left"
                              onClick={() => onLessonOpen(book.id, lesson.id)}
                            >
                              <span className="ml-3 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray1 text-gray7">
                                {isLessonOpened ? (
                                  <ChevronDownIcon className="h-4 w-4" />
                                ) : (
                                  <ChevronRightIcon className="h-4 w-4" />
                                )}
                              </span>

                              <div className="min-w-0">
                                <TextWinds
                                  type="content_body3"
                                  className="truncate"
                                >
                                  {lesson.title}
                                </TextWinds>
                              </div>
                            </button>

                            <Checkbox
                              checked={lessonAllSelected}
                              indeterminate={lessonPartiallySelected}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() =>
                                onLessonSelect(book.id, lesson.id)
                              }
                            >
                              <TextWinds type="caption1" color="gray8">
                                전체 선택
                              </TextWinds>
                            </Checkbox>
                          </div>

                          {isLessonOpened && lesson.isAssignmentLoaded && (
                            <div className="mx-4 mb-3 ml-12 flex flex-col overflow-hidden rounded-lg border border-gray2 bg-gray0">
                              {lesson.assignments?.map((assignment) => (
                                <label
                                  key={assignment.id}
                                  className="flex cursor-pointer items-center gap-3 border-b border-gray2 px-3 py-2.5 last:border-b-0 hover:bg-white"
                                >
                                  <Checkbox
                                    checked={selectedAssignmentIds.has(
                                      assignment.id,
                                    )}
                                    onChange={() =>
                                      onAssignmentSelect(assignment.id)
                                    }
                                  />

                                  <div className="min-w-0">
                                    <TextWinds
                                      type="caption1"
                                      className="block truncate"
                                    >
                                      {assignment.title}
                                    </TextWinds>
                                  </div>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default RecitationSelectionPanel;
